import React, { useState, useEffect } from "react";
import {
  FluentProvider,
  webLightTheme,
  makeStyles,
  tokens,
  Text,
  Input,
  Checkbox,
  Link,
  Dropdown,
  Option,
  Tooltip,
} from "@fluentui/react-components";
import {
  ArrowClockwiseRegular,
  ArrowDownloadRegular,
  DocumentRegular,
  PeopleRegular,
  EditRegular,
  QuestionCircleRegular,
  ChatRegular,
  DismissRegular,
  SearchRegular,
  ChevronDownRegular,
  ArrowSortUpRegular,
  CheckmarkCircleFilled,
  ChevronRightRegular,
  HomeRegular,
  GridRegular,
  ShieldPersonRegular,
  LinkRegular,
  WrenchRegular,
  ChevronUpRegular,
  ReceiptRegular,
  ClockRegular,
  CalendarRegular,
  PersonRegular,
  StarRegular,
  StarFilled,
  SettingsRegular,
  HeadphonesRegular,
  InfoRegular,
  CreditCardPersonRegular,
  AddRegular,
  MoreHorizontalRegular,
  WalletRegular,
  MoneyRegular,
  BuildingBankRegular,
  TagRegular,
  SlideTextRegular,
  Folder24Regular,
  CreditCardClock24Regular,
} from "@fluentui/react-icons";

// ─── Types ────────────────────────────────────────────────────────────────────
type PageKey =
  | "overview" | "iam" | "billingScopes" | "diagnose"
  | "costAnalysis" | "costAlerts" | "budgets" | "advisorRec"
  | "invoices" | "paymentMethods" | "paymentHistory"
  | "reservationTx" | "billingProfiles" | "benefits"
  | "allSubscriptions" | "reservations" | "savingsPlans"
  | "properties" | "exports" | "costAllocation" | "transfers"
  | "supportRequest";

// ─── Styles ───────────────────────────────────────────────────────────────────
const useStyles = makeStyles({
  appRoot: {
    display: "flex", flexDirection: "column",
    height: "100vh", overflow: "hidden",
    fontFamily: tokens.fontFamilyBase,
  },
  shell: { display: "flex", flex: 1, overflow: "hidden" },

  // sidebar
  sidebar: {
    width: "220px", flexShrink: 0, backgroundColor: "#fff",
    borderRight: `1px solid ${tokens.colorNeutralStroke2}`,
    display: "flex", flexDirection: "column", overflow: "hidden",
  },
  sidebarHeader: { padding: "12px", borderBottom: `1px solid ${tokens.colorNeutralStroke2}` },
  sidebarBrand: { display: "flex", alignItems: "center", gap: "8px", marginBottom: "8px" },
  sidebarBrandIcon: {
    width: "28px", height: "28px", backgroundColor: "#0078d4",
    borderRadius: "4px", display: "flex", alignItems: "center",
    justifyContent: "center", color: "#fff", fontSize: "14px", flexShrink: 0,
  },
  sidebarBrandName: { fontSize: "13px", fontWeight: "600", color: "#323130", lineHeight: "1.2" },
  sidebarBrandSub: { fontSize: "11px", color: "#605e5c" },
  sidebarSearch: { width: "100%" },
  navScroll: { flex: 1, overflowY: "auto", padding: "4px 0" },
  navGroup: { marginBottom: "2px" },
  navGroupHeader: {
    display: "flex", alignItems: "center", gap: "6px",
    padding: "6px 12px", fontSize: "12px", color: "#323130",
    fontWeight: "600", cursor: "pointer", userSelect: "none",
    ":hover": { backgroundColor: "#f3f2f1" },
  },
  navGroupIcon: { color: "#605e5c", fontSize: "14px", width: "16px", flexShrink: 0 },
  navItem: {
    display: "flex", alignItems: "center", gap: "8px",
    padding: "6px 12px 6px 28px", fontSize: "12px",
    color: "#323130", cursor: "pointer", position: "relative",
    transition: "background-color 0.12s ease",
    ":hover": { backgroundColor: "#f3f2f1" },
  },
  navItemActive: { backgroundColor: "#e6f2fb", color: "#0078d4", fontWeight: "600" },
  navItemIcon: { fontSize: "14px", width: "16px", flexShrink: 0 },
  sidebarFooter: {
    padding: "8px 12px", fontSize: "10px", color: "#a19f9d",
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  kbd: {
    background: "#edebe9", border: "1px solid #c8c6c4",
    borderRadius: "2px", padding: "0 4px", fontSize: "10px",
  },

  // main
  main: { flex: 1, display: "flex", flexDirection: "column", overflow: "hidden", backgroundColor: "#fff" },
  breadcrumb: {
    padding: "6px 16px", fontSize: "12px", color: "#605e5c",
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    display: "flex", alignItems: "center", gap: "4px",
    backgroundColor: "#fff", flexShrink: 0,
  },
  bladeHeader: { padding: "16px 20px 0", flexShrink: 0 },
  bladeIconTitle: { display: "flex", alignItems: "center", gap: "12px", marginBottom: "2px" },
  bladeIcon: {
    width: "36px", height: "36px", backgroundColor: "#0078d4",
    borderRadius: "4px", display: "flex", alignItems: "center",
    justifyContent: "center", color: "#fff", flexShrink: 0,
  },
  bladeTitle: { fontSize: "20px", fontWeight: "600", color: "#323130" },
  bladeTitleMuted: { fontWeight: "400", color: "#605e5c" },
  bladeSubtitle: { fontSize: "12px", color: "#605e5c", marginBottom: "12px", paddingLeft: "48px" },

  // command bar
  commandBar: {
    display: "flex", alignItems: "center", gap: "4px",
    padding: "6px 20px", borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
    backgroundColor: "#fff", flexWrap: "wrap", flexShrink: 0,
  },
  cmdBtn: {
    display: "flex", alignItems: "center", gap: "5px",
    padding: "4px 10px", fontSize: "12px", color: "#323130",
    border: "none", background: "transparent", cursor: "pointer",
    borderRadius: "2px", height: "30px",
    transition: "background-color 0.1s ease",
    ":hover": { backgroundColor: "#f3f2f1" },
  },
  cmdBtnDisabled: {
    display: "flex", alignItems: "center", gap: "5px",
    padding: "4px 10px", fontSize: "12px", color: "#a19f9d",
    border: "none", background: "transparent",
    cursor: "not-allowed", borderRadius: "2px", height: "30px",
  },
  cmdBtnIcon: { fontSize: "14px", color: "#0078d4" },
  cmdBtnIconDisabled: { fontSize: "14px", color: "#a19f9d" },
  cmdDivider: { width: "1px", height: "20px", backgroundColor: "#c8c6c4", margin: "0 4px" },

  // content
  contentOuter: { flex: 1, overflowY: "auto", padding: "0" },

  // tabs
  tabs: {
    display: "flex", padding: "0 20px",
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`, flexShrink: 0,
  },
  tab: {
    padding: "10px 16px", fontSize: "13px", color: "#323130",
    cursor: "pointer", borderBottom: "2px solid transparent",
    marginBottom: "-1px", fontWeight: "400",
    transition: "color 0.15s ease, border-color 0.15s ease",
  },
  tabActive: { color: "#0078d4", borderBottom: "2px solid #0078d4", fontWeight: "600" },

  // banners
  messages: { margin: "12px 20px 0", display: "flex", flexDirection: "column", gap: "6px" },
  infoMsg: {
    display: "flex", alignItems: "flex-start", gap: "8px",
    padding: "8px 12px", backgroundColor: "#e6f2fb",
    border: "1px solid #b3d6f2", borderRadius: "2px",
    fontSize: "12px", color: "#323130", lineHeight: "1.5",
  },
  infoMsgClose: {
    marginLeft: "auto", flexShrink: 0, cursor: "pointer",
    color: "#605e5c", background: "none", border: "none", padding: 0,
  },
  description: { padding: "12px 20px", fontSize: "13px", color: "#323130", lineHeight: "1.6" },
  amountDue: {
    padding: "0 20px 12px", borderLeft: "3px solid #0078d4",
    marginLeft: "20px", marginRight: "20px", marginBottom: "12px",
  },
  amountLabel: { fontSize: "12px", color: "#605e5c", marginBottom: "2px" },
  amountValue: { fontSize: "24px", fontWeight: "700", color: "#323130" },

  // filter
  filterBar: { display: "flex", alignItems: "center", gap: "8px", padding: "8px 20px", flexWrap: "wrap" },
  filterPill: {
    display: "inline-flex", alignItems: "center", gap: "4px",
    padding: "4px 10px", border: "1px solid #c8c6c4",
    borderRadius: "14px", backgroundColor: "#e6f2fb",
    fontSize: "12px", color: "#323130", cursor: "pointer",
  },
  filterPillChevron: { fontSize: "11px", color: "#605e5c" },
  filterRight: { marginLeft: "auto", display: "flex", alignItems: "center", gap: "8px" },
  excludeLabel: { fontSize: "12px", color: "#323130", display: "flex", alignItems: "center", gap: "4px" },

  // table
  tableWrap: { padding: "0 20px" },
  resultCount: { fontSize: "12px", color: "#323130", margin: "4px 0 8px" },
  table: { width: "100%", borderCollapse: "collapse", fontSize: "12px" },
  th: {
    padding: "8px 10px", textAlign: "left", fontSize: "12px",
    fontWeight: "600", color: "#323130",
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    whiteSpace: "nowrap", userSelect: "none", cursor: "pointer",
  },
  thCheck: { width: "32px", padding: "8px 10px", borderBottom: `1px solid ${tokens.colorNeutralStroke2}` },
  thSort: { display: "inline-flex", alignItems: "center", gap: "4px" },
  td: { padding: "8px 10px", color: "#323130", verticalAlign: "middle", borderBottom: "1px solid #f3f2f1" },
  tdCheck: { width: "32px", padding: "8px 10px", borderBottom: "1px solid #f3f2f1", verticalAlign: "middle" },
  tr: { transition: "background-color 0.1s ease", ":hover": { backgroundColor: "#f3f2f1" } },
  statusPaid: { display: "inline-flex", alignItems: "center", gap: "4px", color: "#107c10", fontSize: "12px" },
  helpLink: { display: "flex", justifyContent: "flex-end", padding: "4px 20px 8px" },

  // payment cards
  cardRow: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px", marginBottom: "28px" },
  cardSectionTitle: { fontSize: "14px", fontWeight: "600", color: "#323130", marginBottom: "4px" },
  cardSectionDesc: { fontSize: "12px", color: "#605e5c", marginBottom: "12px", lineHeight: "1.5" },
  card: {
    border: `1px solid ${tokens.colorNeutralStroke2}`, borderRadius: "4px",
    padding: "16px 20px", backgroundColor: "#fff",
    boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
    minHeight: "140px", display: "flex", flexDirection: "column", justifyContent: "space-between",
  },
  cardTop: { display: "flex", alignItems: "flex-start", gap: "12px", marginBottom: "12px" },
  cardLogoVisa: {
    fontSize: "13px", fontWeight: "900", fontStyle: "italic",
    color: "#1a1f71", letterSpacing: "-0.5px",
    flexShrink: 0, lineHeight: "1", marginTop: "2px",
  },
  cardInfo: { flex: 1 },
  cardName: { fontSize: "14px", fontWeight: "600", color: "#323130", marginBottom: "2px" },
  cardSub: { fontSize: "12px", color: "#605e5c" },
  cardActions: { display: "flex", gap: "12px", marginTop: "auto" },
  creditsIcon: {
    width: "40px", height: "40px", borderRadius: "8px", background: "#7b68ee",
    display: "flex", alignItems: "center", justifyContent: "center",
    color: "#fff", fontSize: "20px", flexShrink: 0,
  },
  creditsBalance: { fontSize: "14px", fontWeight: "600", color: "#323130", marginBottom: "2px" },
  creditsSub: { fontSize: "13px", color: "#605e5c", marginBottom: "12px" },
  sectionTitle: { fontSize: "15px", fontWeight: "600", color: "#323130", marginBottom: "4px" },
  sectionDesc: { fontSize: "12px", color: "#605e5c", marginBottom: "14px", lineHeight: "1.5" },
  paymentMethodCell: { display: "flex", alignItems: "center", gap: "6px" },
  visaLabel: {
    fontSize: "11px", fontWeight: "900", fontStyle: "italic",
    color: "#1a1f71", border: "1.5px solid #1a1f71",
    borderRadius: "2px", padding: "0 3px", lineHeight: "1.4",
  },
  starIcon: { color: "#f5a623", fontSize: "13px" },
  moreBtn: {
    background: "none", border: "none", cursor: "pointer",
    color: "#605e5c", fontSize: "16px", padding: "4px",
    borderRadius: "2px", ":hover": { backgroundColor: "#f3f2f1" },
  },
  expiredBadge: { color: "#a4262c", fontSize: "12px" },

  // placeholder
  placeholder: {
    display: "flex", flexDirection: "column", alignItems: "center",
    justifyContent: "center", height: "300px", gap: "12px",
  },
  placeholderIcon: { fontSize: "48px", color: "#c8c6c4" },
  placeholderTitle: { fontSize: "16px", fontWeight: "600", color: "#323130" },
  placeholderDesc: { fontSize: "13px", color: "#605e5c", textAlign: "center", maxWidth: "360px" },

  // payment history pill
  phPill: {
    display: "inline-flex", alignItems: "center", gap: "3px",
    padding: "4px 12px", border: "1px solid #c8c6c4",
    borderRadius: "14px", backgroundColor: "#fff",
    fontSize: "12px", color: "#323130", cursor: "pointer",
    transition: "background-color 0.1s ease",
    ":hover": { backgroundColor: "#f3f2f1" },
  },
  phPillLabel: { color: "#605e5c" },
  phPillValue: { fontWeight: "600" },

  // pagination footer
  phFooter: {
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
    padding: "10px 20px",
    display: "flex", alignItems: "center", gap: "8px",
    backgroundColor: "#fff", flexShrink: 0,
  },
  phPageBtn: {
    padding: "4px 12px", fontSize: "12px",
    border: `1px solid ${tokens.colorNeutralStroke2}`, borderRadius: "2px",
    backgroundColor: "#0078d4", color: "#fff",
    cursor: "not-allowed", opacity: 0.45,
  },
  phPageDropdown: {
    border: `1px solid ${tokens.colorNeutralStroke2}`, borderRadius: "2px",
    padding: "3px 24px 3px 8px", fontSize: "12px",
    color: "#a19f9d", minWidth: "44px", position: "relative",
    display: "inline-flex", alignItems: "center",
  },
});

// ─── Animation keyframe (injected once) ──────────────────────────────────────
function usePageAnimation() {
  useEffect(() => {
    const id = "az-billing-kf";
    if (!document.getElementById(id)) {
      const el = document.createElement("style");
      el.id = id;
      el.textContent = `
        @keyframes azFadeIn {
          from { opacity: 0; transform: translateY(8px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .az-page-enter { animation: azFadeIn 0.2s ease forwards; }
      `;
      document.head.appendChild(el);
    }
  }, []);
}

// ─── Page label map ───────────────────────────────────────────────────────────
const PAGE_LABELS: Record<PageKey, string> = {
  overview: "Overview", iam: "Access control (IAM)", billingScopes: "Billing scopes",
  diagnose: "Diagnose and solve problems", costAnalysis: "Cost analysis",
  costAlerts: "Cost alerts", budgets: "Budgets", advisorRec: "Advisor recommendations",
  invoices: "Invoices", paymentMethods: "Payment methods", paymentHistory: "Payment history",
  reservationTx: "Reservation transactions", billingProfiles: "Billing profiles",
  benefits: "Benefits", allSubscriptions: "All billing subscriptions",
  reservations: "Reservations + Hybrid Benefit", savingsPlans: "Savings plans",
  properties: "Properties", exports: "Exports", costAllocation: "Cost allocation",
  transfers: "Transfer requests", supportRequest: "New support request",
};

// ─── Page icon map ────────────────────────────────────────────────────────────
const PAGE_ICONS: Record<PageKey, React.ReactNode> = {
  overview: <GridRegular />,          iam: <ShieldPersonRegular />,
  billingScopes: <LinkRegular />,     diagnose: <WrenchRegular />,
  costAnalysis: <MoneyRegular />,     costAlerts: <BuildingBankRegular />,
  budgets: <TagRegular />,            advisorRec: <StarRegular />,
  invoices: <ReceiptRegular />,       paymentMethods: <CreditCardPersonRegular />,
  paymentHistory: <ClockRegular />,   reservationTx: <CalendarRegular />,
  billingProfiles: <PersonRegular />, benefits: <StarRegular />,
  allSubscriptions: <Folder24Regular />, reservations: <Folder24Regular />,
  savingsPlans: <Folder24Regular />,  properties: <SettingsRegular />,
  exports: <ArrowDownloadRegular />,  costAllocation: <SlideTextRegular />,
  transfers: <ArrowClockwiseRegular />, supportRequest: <HeadphonesRegular />,
};

// ─── Nav config ───────────────────────────────────────────────────────────────
interface NavItemCfg { key: PageKey; label: string; icon: React.ReactNode }
interface NavGroupCfg { label: string; items: NavItemCfg[]; defaultOpen?: boolean }

const QUICK_ACCESS: NavItemCfg[] = [
  { key: "overview",      label: "Overview",                    icon: <GridRegular /> },
  { key: "iam",           label: "Access control (IAM)",        icon: <ShieldPersonRegular /> },
  { key: "billingScopes", label: "Billing scopes",              icon: <LinkRegular /> },
  { key: "diagnose",      label: "Diagnose and solve problems", icon: <WrenchRegular /> },
];

const NAV_GROUPS: NavGroupCfg[] = [
  {
    label: "Cost management",
    items: [
      { key: "costAnalysis", label: "Cost analysis",           icon: <MoneyRegular /> },
      { key: "costAlerts",   label: "Cost alerts",             icon: <BuildingBankRegular /> },
      { key: "budgets",      label: "Budgets",                 icon: <TagRegular /> },
      { key: "advisorRec",   label: "Advisor recommendations", icon: <StarRegular /> },
    ],
  },
  {
    label: "Billing",
    defaultOpen: true,
    items: [
      { key: "invoices",        label: "Invoices",                 icon: <ReceiptRegular /> },
      { key: "paymentMethods",  label: "Payment methods",          icon: <CreditCardPersonRegular /> },
      { key: "paymentHistory",  label: "Payment history",          icon: <ClockRegular /> },
      { key: "reservationTx",   label: "Reservation transactions", icon: <CalendarRegular /> },
      { key: "billingProfiles", label: "Billing profiles",         icon: <PersonRegular /> },
      { key: "benefits",        label: "Benefits",                 icon: <StarRegular /> },
    ],
  },
  {
    label: "Products + services",
    items: [
      { key: "allSubscriptions", label: "All billing subscriptions",     icon: <Folder24Regular /> },
      { key: "reservations",     label: "Reservations + Hybrid Benefit", icon: <Folder24Regular /> },
      { key: "savingsPlans",     label: "Savings plans",                 icon: <Folder24Regular /> },
    ],
  },
  {
    label: "Settings",
    items: [
      { key: "properties",     label: "Properties",        icon: <SettingsRegular /> },
      { key: "exports",        label: "Exports",            icon: <ArrowDownloadRegular /> },
      { key: "costAllocation", label: "Cost allocation",   icon: <SlideTextRegular /> },
      { key: "transfers",      label: "Transfer requests", icon: <ArrowClockwiseRegular /> },
    ],
  },
  {
    label: "Support + troubleshooting",
    items: [{ key: "supportRequest", label: "New support request", icon: <HeadphonesRegular /> }],
  },
];

// ─── NavGroup sub-component ───────────────────────────────────────────────────
const NavGroupComp: React.FC<{
  group: NavGroupCfg;
  activePage: PageKey;
  onNavigate: (key: PageKey) => void;
}> = ({ group, activePage, onNavigate }) => {
  const styles = useStyles();
  const [open, setOpen] = useState(group.defaultOpen ?? false);

  return (
    <div className={styles.navGroup}>
      <div
        className={styles.navGroupHeader}
        onClick={() => setOpen((o) => !o)}
        role="button"
        aria-expanded={open}
      >
        <span className={styles.navGroupIcon}>
          {open ? <ChevronUpRegular /> : <ChevronRightRegular />}
        </span>
        {group.label}
      </div>
      <div style={{
        overflow: "hidden",
        maxHeight: open ? `${group.items.length * 36}px` : "0",
        transition: "max-height 0.22s ease",
      }}>
        {group.items.map((item) => {
          const isActive = activePage === item.key;
          return (
            <div
              key={item.key}
              className={`${styles.navItem} ${isActive ? styles.navItemActive : ""}`}
              onClick={() => onNavigate(item.key)}
              role="button"
              aria-current={isActive ? "page" : undefined}
            >
              {isActive && (
                <span style={{
                  position: "absolute", left: 0, top: 0, bottom: 0,
                  width: "2px", backgroundColor: "#0078d4",
                }} />
              )}
              <span className={styles.navItemIcon}>{item.icon}</span>
              {item.label}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ─── Invoice data ─────────────────────────────────────────────────────────────
interface Invoice {
  id: string; date: string; period: string;
  profile: string; amount: string; statusLabel: string; pay: string;
}
const INVOICES: Invoice[] = [
  { id: "G112537326", date: "9/9/2025",  period: "8/1/2025 – 8/3...", profile: "Chigozie Iloelunachi", amount: "$0.00",  statusLabel: "No payment needed", pay: "N/A" },
  { id: "G106177190", date: "8/9/2025",  period: "7/1/2025 – 7/3...", profile: "Chigozie Iloelunachi", amount: "$0.00",  statusLabel: "No payment needed", pay: "N/A" },
  { id: "G101571440", date: "7/9/2025",  period: "6/1/2025 – 6/3...", profile: "Chigozie Iloelunachi", amount: "$0.00",  statusLabel: "No payment needed", pay: "N/A" },
  { id: "G095620290", date: "6/9/2025",  period: "5/21/2025 – 5/...", profile: "Chigozie Iloelunachi", amount: "$31.42", statusLabel: "Paid on 7/1/2025",   pay: "N/A" },
];

// ─── Invoices page ────────────────────────────────────────────────────────────
const InvoicesPage: React.FC = () => {
  const styles = useStyles();
  const [msg1, setMsg1] = useState(true);
  const [msg2, setMsg2] = useState(true);
  const [excludeZero, setExcludeZero] = useState(false);
  const [allChecked, setAllChecked] = useState(false);
  const [checked, setChecked] = useState<Set<string>>(new Set());
  const toggleRow = (id: string) =>
    setChecked((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const filtered = INVOICES.filter((inv) => !excludeZero || inv.amount !== "$0.00");

  return (
    <>
      <div className={styles.commandBar} role="toolbar" aria-label="Invoice actions">
        {[
          { icon: <ArrowClockwiseRegular className={styles.cmdBtnIcon} />, label: "Refresh" },
          { icon: <ArrowDownloadRegular className={styles.cmdBtnIcon} />, label: "Export to CSV" },
          { icon: <DocumentRegular className={styles.cmdBtnIcon} />, label: "Download" },
          { icon: <PeopleRegular className={styles.cmdBtnIcon} />, label: "Manage access" },
          { icon: <EditRegular className={styles.cmdBtnIcon} />, label: "Edit invoice details" },
        ].map(({ icon, label }) => (
          <button key={label} className={styles.cmdBtn} aria-label={label}>
            {icon}<span>{label}</span>
            {label === "Edit invoice details" && <ChevronDownRegular style={{ fontSize: "11px", marginLeft: "2px" }} />}
          </button>
        ))}
        <div className={styles.cmdDivider} />
        <button className={styles.cmdBtn}><QuestionCircleRegular className={styles.cmdBtnIcon} /><span>Help</span></button>
        <button className={styles.cmdBtn}><ChatRegular className={styles.cmdBtnIcon} /><span>Feedback</span></button>
      </div>

      <div className={styles.contentOuter}>
        <div className="az-page-enter">
          <div className={styles.messages}>
            {msg1 && (
              <div className={styles.infoMsg}>
                <InfoRegular style={{ fontSize: "14px", color: "#0078d4", flexShrink: 0, marginTop: "1px" }} />
                <span>
                  A new version of the Azure Price Sheet is now available for download for billing period January 2023 onwards,
                  including Azure Reserved Instance pricing and an updated schema.{" "}
                  <Link href="#">Learn more</Link>
                </span>
                <button className={styles.infoMsgClose} onClick={() => setMsg1(false)} aria-label="Dismiss">
                  <DismissRegular style={{ fontSize: "14px" }} />
                </button>
              </div>
            )}
            {msg2 && (
              <div className={styles.infoMsg}>
                <InfoRegular style={{ fontSize: "14px", color: "#0078d4", flexShrink: 0, marginTop: "1px" }} />
                <span>
                  At renewal, you may see two invoices for the same license-based subscription purchased through
                  Microsoft 365 admin center or Azure Marketplace due to a switch from billing in arrears to billing upfront.{" "}
                  <Link href="#">Learn more about this change to the billing schedule</Link>
                </span>
                <button className={styles.infoMsgClose} onClick={() => setMsg2(false)} aria-label="Dismiss">
                  <DismissRegular style={{ fontSize: "14px" }} />
                </button>
              </div>
            )}
          </div>

          <p className={styles.description}>
            View invoices for your billing account. A monthly invoice is generated at the beginning of the calendar
            month for each billing profile. If you have Azure credits, they are automatically applied, reducing the
            amount due. The payment instructions are provided on the invoice.{" "}
            <Link href="#">Learn more about invoice timing</Link>
          </p>

          <div className={styles.amountDue}>
            <div className={styles.amountLabel}>Amount due</div>
            <div className={styles.amountValue}>$0.00</div>
          </div>

          <div className={styles.filterBar}>
            <Input size="small" placeholder="Search" style={{ width: "180px" }}
              contentBefore={<SearchRegular style={{ fontSize: "14px", color: "#605e5c" }} />} />
            {["Status: All", "Billing profile: All", "Timespan: Last 12 months"].map((label) => (
              <div key={label} className={styles.filterPill}>
                {label}<ChevronDownRegular className={styles.filterPillChevron} />
              </div>
            ))}
            <div className={styles.filterRight}>
              <label className={styles.excludeLabel}>
                <Checkbox checked={excludeZero} onChange={(_, d) => setExcludeZero(!!d.checked)} size="medium" />
                Exclude $0 invoices
                <Tooltip content="Invoices with zero total amount will be hidden" relationship="label">
                  <InfoRegular style={{ fontSize: "13px", color: "#605e5c", cursor: "help" }} />
                </Tooltip>
              </label>
              <Dropdown size="small" defaultValue="List" style={{ minWidth: "80px" }}>
                <Option>List</Option>
                <Option>Cards</Option>
              </Dropdown>
            </div>
          </div>

          <div className={styles.tableWrap}>
            <div className={styles.resultCount}>Showing 1 to {filtered.length} of {filtered.length} results</div>
            <div className={styles.helpLink}>
              <Link href="#" style={{ fontSize: "12px" }}>Help me understand this table</Link>
            </div>
            <table className={styles.table} aria-label="Invoices">
              <thead>
                <tr>
                  <th className={styles.thCheck}>
                    <Checkbox checked={allChecked}
                      onChange={(_, d) => { setAllChecked(!!d.checked); setChecked(d.checked ? new Set(INVOICES.map(i => i.id)) : new Set()); }}
                      aria-label="Select all" />
                  </th>
                  {["Invoice ID", "Invoice date", "Billing peri...", "Billing profile", "Total amount", "Status", "Pay"].map((col) => (
                    <th key={col} className={styles.th}>
                      <span className={styles.thSort}>
                        {col}
                        <ArrowSortUpRegular style={{ fontSize: "11px", color: "#605e5c" }} />
                        <ChevronDownRegular style={{ fontSize: "10px", color: "#605e5c" }} />
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((inv) => (
                  <tr key={inv.id} className={styles.tr}>
                    <td className={styles.tdCheck}>
                      <Checkbox checked={checked.has(inv.id)} onChange={() => toggleRow(inv.id)} aria-label={`Select ${inv.id}`} />
                    </td>
                    <td className={styles.td}><Link href="#" style={{ fontSize: "12px" }}>{inv.id}</Link></td>
                    <td className={styles.td}>{inv.date}</td>
                    <td className={styles.td}>{inv.period}</td>
                    <td className={styles.td}><Link href="#" style={{ fontSize: "12px" }}>{inv.profile}</Link></td>
                    <td className={styles.td}>{inv.amount}</td>
                    <td className={styles.td}>
                      <span className={styles.statusPaid}>
                        <CheckmarkCircleFilled style={{ fontSize: "14px", color: "#107c10" }} />
                        <Link href="#" style={{ fontSize: "12px", color: "#107c10" }}>{inv.statusLabel}</Link>
                      </span>
                    </td>
                    <td className={styles.td}>{inv.pay}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </>
  );
};

// ─── Payment Methods page ─────────────────────────────────────────────────────
const PaymentMethodsPage: React.FC = () => {
  const styles = useStyles();
  const [activeTab, setActiveTab] = useState<"methods" | "credits">("methods");

  return (
    <>
      <div className={styles.commandBar} role="toolbar" aria-label="Payment method actions">
        <button className={styles.cmdBtn}><QuestionCircleRegular className={styles.cmdBtnIcon} /><span>Troubleshoot</span></button>
        <button className={styles.cmdBtn}><AddRegular className={styles.cmdBtnIcon} /><span>Add payment method</span></button>
        <button className={styles.cmdBtnDisabled} disabled>
          <CreditCardClock24Regular className={styles.cmdBtnIconDisabled} />
          <span>Request approval to pay by wire transfer</span>
        </button>
      </div>

      <div className={styles.tabs} role="tablist">
        {(["methods", "credits"] as const).map((tab) => (
          <div key={tab} role="tab" aria-selected={activeTab === tab}
            className={`${styles.tab} ${activeTab === tab ? styles.tabActive : ""}`}
            onClick={() => setActiveTab(tab)}
          >
            {tab === "methods" ? "Payment methods" : "Azure credits"}
          </div>
        ))}
      </div>

      <div className={styles.contentOuter}>
        <div key={activeTab} className="az-page-enter" style={{ padding: "20px" }}>
          {activeTab === "methods" ? (
            <>
              <div className={styles.cardRow}>
                <div>
                  <div className={styles.cardSectionTitle}>Default payment method</div>
                  <div className={styles.cardSectionDesc}>Your default payment method will be automatically charged each month.</div>
                  <div className={styles.card}>
                    <div className={styles.cardTop}>
                      <span className={styles.cardLogoVisa}>VISA</span>
                      <div className={styles.cardInfo}>
                        <div className={styles.cardName}>Visa ****5488</div>
                        <div className={styles.cardSub}>Expires on 2/2026</div>
                      </div>
                    </div>
                    <div className={styles.cardActions}>
                      <Link href="#" style={{ fontSize: "12px" }}>Replace &gt;</Link>
                      <Link href="#" style={{ fontSize: "12px" }}>Detach &gt;</Link>
                    </div>
                  </div>
                </div>
                <div>
                  <div className={styles.cardSectionTitle}>Azure credits</div>
                  <div className={styles.cardSectionDesc}>Your Azure credits will be used before your default payment method.</div>
                  <div className={styles.card}>
                    <div className={styles.cardTop}>
                      <div className={styles.creditsIcon}><WalletRegular style={{ fontSize: "20px" }} /></div>
                      <div className={styles.cardInfo}>
                        <div className={styles.creditsBalance}>Available balance</div>
                        <div className={styles.creditsSub}>$1,000.00</div>
                      </div>
                    </div>
                    <div className={styles.cardActions}>
                      <Link href="#" style={{ fontSize: "12px" }}>View details &gt;</Link>
                    </div>
                  </div>
                </div>
              </div>

              <div className={styles.sectionTitle}>Your payment methods</div>
              <div className={styles.sectionDesc}>
                Select the ellipsis (...) to edit, delete or make it the default payment method of this billing profile.
              </div>
              <table className={styles.table} aria-label="Payment methods">
                <thead>
                  <tr>
                    {["Payment method", "Name on card", "Expiration date", "Expiry status", "Type", ""].map((col, i) => (
                      <th key={i} className={styles.th}>{col}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  <tr className={styles.tr}>
                    <td className={styles.td}>
                      <div className={styles.paymentMethodCell}>
                        <span className={styles.visaLabel}>VISA</span>
                        <span>Visa ****5488</span>
                        <StarFilled className={styles.starIcon} aria-label="Default" />
                      </div>
                    </td>
                    <td className={styles.td}>JUDE ILOEH</td>
                    <td className={styles.td}>2/2026</td>
                    <td className={styles.td}><span className={styles.expiredBadge}>Expired</span></td>
                    <td className={styles.td}>CreditCard</td>
                    <td className={styles.td} style={{ textAlign: "right" }}>
                      <button className={styles.moreBtn} aria-label="More options"><MoreHorizontalRegular /></button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </>
          ) : (
            <>
              <div className={styles.sectionTitle}>Azure credits</div>
              <div className={styles.sectionDesc}>
                Azure credits are applied to your invoice before your default payment method is charged.
              </div>
              <table className={styles.table} aria-label="Azure credits">
                <thead>
                  <tr>
                    {["Credit name", "Original amount", "Remaining balance", "Expiration date", "Status"].map((col) => (
                      <th key={col} className={styles.th}>{col}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  <tr className={styles.tr}>
                    <td className={styles.td}>Azure credit offer</td>
                    <td className={styles.td}>$1,000.00</td>
                    <td className={styles.td}>$1,000.00</td>
                    <td className={styles.td}>12/31/2026</td>
                    <td className={styles.td} style={{ color: "#107c10" }}>Active</td>
                  </tr>
                </tbody>
              </table>
            </>
          )}
        </div>
      </div>
    </>
  );
};

// ─── Payment History page ─────────────────────────────────────────────────────
const PaymentHistoryPage: React.FC = () => {
  const styles = useStyles();
  const [search, setSearch] = useState("");

  const pills: { label: string; value: string }[] = [
    { label: "Timespan",        value: "Last 12 months" },
    { label: "Payment method",  value: "All" },
    { label: "Invoice ID",      value: "All" },
    { label: "Type",            value: "All" },
    { label: "Billing profile", value: "All" },
  ];

  return (
    <>
      {/* Command bar — Refresh + Export to CSV only (matches screenshot) */}
      <div className={styles.commandBar} role="toolbar" aria-label="Payment history actions">
        <button className={styles.cmdBtn}>
          <ArrowClockwiseRegular className={styles.cmdBtnIcon} /><span>Refresh</span>
        </button>
        <button className={styles.cmdBtn}>
          <ArrowDownloadRegular className={styles.cmdBtnIcon} /><span>Export to CSV</span>
        </button>
      </div>

      {/* Scrollable content */}
      <div className={styles.contentOuter}>
        <div className="az-page-enter">

          {/* Description */}
          <p className={styles.description}>
            View the payment history for your billing account. It might take some time for a payment to show up in the portal.
          </p>

          {/* Search + filter pills */}
          <div style={{ display: "flex", alignItems: "center", gap: "8px", padding: "0 20px 12px", flexWrap: "wrap" }}>
            <Input
              size="small"
              placeholder="Any text search"
              value={search}
              onChange={(_, d) => setSearch(d.value)}
              style={{ width: "200px" }}
              contentBefore={<SearchRegular style={{ fontSize: "14px", color: "#605e5c" }} />}
            />
            {pills.map((p) => (
              <button
                key={p.label}
                className={styles.phPill}
                type="button"
                aria-label={`${p.label} filter: ${p.value}`}
              >
                <span className={styles.phPillLabel}>{p.label}</span>
                <span style={{ color: "#605e5c", margin: "0 1px" }}>:</span>
                <span className={styles.phPillValue}>{p.value}</span>
                <ChevronDownRegular style={{ fontSize: "10px", color: "#605e5c", marginLeft: "3px" }} />
              </button>
            ))}
          </div>

          {/* Result count */}
          <div style={{ padding: "0 20px 20px", fontSize: "12px", color: "#323130" }}>
            Showing 0 to 0 of 0 results
          </div>

          {/* Empty-state illustration — credit cards + orange clock */}
          <div style={{
            display: "flex", flexDirection: "column", alignItems: "center",
            justifyContent: "center", paddingBottom: "32px", gap: "14px",
          }}>
            <svg width="200" height="160" viewBox="0 0 200 160" fill="none"
              xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
              {/* Back card – dark blue */}
              <rect x="14" y="16" width="128" height="82" rx="8" fill="#0078d4" />
              {/* Back card stripe */}
              <rect x="14" y="30" width="128" height="16" fill="#005a9e" />

              {/* Front card – light blue */}
              <rect x="34" y="36" width="128" height="82" rx="8" fill="#50e6ff" />
              {/* Front card stripe */}
              <rect x="34" y="50" width="128" height="16" fill="#9cebff" opacity="0.75" />

              {/* Chip on front card */}
              <rect x="50" y="76" width="22" height="16" rx="3" fill="white" opacity="0.9" />
              <line x1="55" y1="76" x2="55" y2="92" stroke="#b0e6f5" strokeWidth="1" />
              <line x1="60" y1="76" x2="60" y2="92" stroke="#b0e6f5" strokeWidth="1" />
              <line x1="65" y1="76" x2="65" y2="92" stroke="#b0e6f5" strokeWidth="1" />
              <line x1="50" y1="82" x2="72" y2="82" stroke="#b0e6f5" strokeWidth="1" />
              <line x1="50" y1="86" x2="72" y2="86" stroke="#b0e6f5" strokeWidth="1" />

              {/* Card number dots */}
              <rect x="50" y="100" width="52" height="5" rx="2.5" fill="white" opacity="0.7" />
              <rect x="50" y="110" width="36" height="4" rx="2" fill="white" opacity="0.5" />

              {/* Small card-type squares top-right of front */}
              <rect x="130" y="76" width="14" height="9" rx="2" fill="white" opacity="0.6" />
              <rect x="148" y="76" width="14" height="9" rx="2" fill="white" opacity="0.45" />

              {/* Clock – outer white circle (covers card edge) */}
              <circle cx="138" cy="116" r="34" fill="white" />
              {/* Clock – orange ring */}
              <circle cx="138" cy="116" r="30" fill="white" stroke="#f78d1e" strokeWidth="6" />
              {/* Clock – face */}
              <circle cx="138" cy="116" r="22" fill="white" />
              {/* Clock tick marks */}
              <line x1="138" y1="96"  x2="138" y2="99"  stroke="#d0d0d0" strokeWidth="2" strokeLinecap="round" />
              <line x1="138" y1="133" x2="138" y2="136" stroke="#d0d0d0" strokeWidth="2" strokeLinecap="round" />
              <line x1="118" y1="116" x2="121" y2="116" stroke="#d0d0d0" strokeWidth="2" strokeLinecap="round" />
              <line x1="155" y1="116" x2="158" y2="116" stroke="#d0d0d0" strokeWidth="2" strokeLinecap="round" />
              {/* Hour hand */}
              <line x1="138" y1="116" x2="138" y2="101" stroke="#b3b3b3" strokeWidth="2.5" strokeLinecap="round" />
              {/* Minute hand */}
              <line x1="138" y1="116" x2="150" y2="122" stroke="#b3b3b3" strokeWidth="2.5" strokeLinecap="round" />
              {/* Center dot */}
              <circle cx="138" cy="116" r="3" fill="#666" />
            </svg>

            <div style={{ fontSize: "16px", fontWeight: "600", color: "#323130" }}>
              No payments to display
            </div>
            <div style={{ fontSize: "13px", color: "#605e5c", textAlign: "center", maxWidth: "340px" }}>
              Try changing your filters if you don't see what you're looking for
            </div>
          </div>
        </div>
      </div>

      {/* Pagination footer — sticky to bottom */}
      <div className={styles.phFooter}>
        <button disabled className={styles.phPageBtn}>&lt; Previous</button>
        <span style={{ fontSize: "12px", color: "#323130", marginLeft: "4px" }}>Page</span>
        <div className={styles.phPageDropdown}>
          <span style={{ fontSize: "12px", color: "#a19f9d" }}>&nbsp;</span>
          <ChevronDownRegular style={{
            position: "absolute", right: "5px",
            fontSize: "11px", color: "#605e5c",
          }} />
        </div>
        <span style={{ fontSize: "12px", color: "#323130" }}>of 0</span>
        <button disabled className={styles.phPageBtn}>Next &gt;</button>
        <div style={{ marginLeft: "auto" }}>
          <Link href="#" style={{ fontSize: "12px", display: "flex", alignItems: "center", gap: "5px", color: "#0078d4" }}>
            <ChatRegular style={{ fontSize: "14px" }} />
            Give feedback
          </Link>
        </div>
      </div>
    </>
  );
};

// ─── Billing Profiles page ────────────────────────────────────────────────────
interface BillingProfile {
  name: string;
  id: string;
  mtdCharges: string;
  lastMonthCharges: string;
  myRole: string;
  status: "Active" | "Disabled";
}

const BILLING_PROFILES: BillingProfile[] = [
  {
    name: "Chigozie Iloelunachi",
    id: "M53U-ZJIU-BG7-PGB",
    mtdCharges: "0.00",
    lastMonthCharges: "0.00",
    myRole: "Billing profile owner",
    status: "Active",
  },
];

const BillingProfilesPage: React.FC = () => {
  const styles = useStyles();
  const [search, setSearch] = useState("");
  const [showDeleted, setShowDeleted] = useState(false);
  const [currentPage] = useState(1);
  const totalPages = 1;

  const filtered = BILLING_PROFILES.filter((p) =>
    !search || p.name.toLowerCase().includes(search.toLowerCase()) || p.id.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <>
      {/* Command bar: Add | Refresh | Export to CSV | separator | Feedback */}
      <div className={styles.commandBar} role="toolbar" aria-label="Billing profiles actions">
        <button className={styles.cmdBtn}>
          <AddRegular className={styles.cmdBtnIcon} /><span>Add</span>
        </button>
        <button className={styles.cmdBtn}>
          <ArrowClockwiseRegular className={styles.cmdBtnIcon} /><span>Refresh</span>
        </button>
        <button className={styles.cmdBtn}>
          <ArrowDownloadRegular className={styles.cmdBtnIcon} /><span>Export to CSV</span>
        </button>
        <div className={styles.cmdDivider} />
        <button className={styles.cmdBtn}>
          <ChatRegular className={styles.cmdBtnIcon} /><span>Feedback</span>
        </button>
      </div>

      <div className={styles.contentOuter}>
        <div className="az-page-enter">
          {/* Description */}
          <p className={styles.description}>
            A billing profile lets you manage your invoice and payment methods. A monthly invoice is generated for each
            billing profile and contains all respective charges from the previous month. Each billing profile has its own
            payment methods that are used to pay its invoice. The charges shown below are estimated amounts based on your
            Azure usage and other purchases and do not include tax.{" "}
            <Link href="https://aka.ms/billingprofileoverview" target="_blank" style={{ fontSize: "13px" }}>
              Learn more
            </Link>
          </p>

          <hr style={{ border: "none", borderTop: "1px solid #e1dfdd", margin: "0 20px 16px" }} />

          {/* Search + Status filter row */}
          <div style={{ display: "flex", alignItems: "flex-end", gap: "12px", padding: "0 20px 12px", flexWrap: "wrap" }}>
            <div>
              <div style={{ fontSize: "12px", color: "#323130", marginBottom: "4px", fontWeight: "400" }}>Search</div>
              <Input
                size="small"
                placeholder="Text search"
                value={search}
                onChange={(_, d) => setSearch(d.value)}
                style={{ width: "200px" }}
              />
            </div>
            <div>
              <div style={{ fontSize: "12px", color: "#323130", marginBottom: "4px", fontWeight: "400" }}>Status</div>
              <div style={{
                display: "flex", alignItems: "center", gap: "6px",
                border: "1px solid #c8c6c4", borderRadius: "2px",
                padding: "4px 8px", backgroundColor: "#fff",
                fontSize: "12px", color: "#323130", cursor: "pointer",
                minWidth: "140px", height: "28px",
              }}>
                <span style={{ flex: 1 }}>Any status</span>
                <ChevronDownRegular style={{ fontSize: "11px", color: "#605e5c" }} />
              </div>
            </div>
          </div>

          {/* Show deleted billing profiles checkbox */}
          <div style={{ padding: "0 20px 12px", display: "flex", alignItems: "center", gap: "6px" }}>
            <Checkbox
              checked={showDeleted}
              onChange={(_, d) => setShowDeleted(!!d.checked)}
              label="Show deleted billing profiles"
            />
          </div>

          {/* Data table */}
          <div className={styles.tableWrap}>
            <table className={styles.table} aria-label="Billing profiles" role="grid">
              <thead>
                <tr>
                  {[
                    { col: "Name", sort: true },
                    { col: "Id", sort: false },
                    { col: "Month-to-date charges", sort: false },
                    { col: "Last month's charges", sort: false },
                    { col: "My Role", sort: false },
                    { col: "Status", sort: true },
                    { col: "", sort: false },
                  ].map(({ col, sort }, i) => (
                    <th key={i} className={styles.th} style={{ cursor: sort ? "pointer" : "default" }}>
                      {sort ? (
                        <span className={styles.thSort}>
                          {col}
                          <ArrowSortUpRegular style={{ fontSize: "11px", color: "#605e5c" }} />
                          <ChevronDownRegular style={{ fontSize: "10px", color: "#605e5c" }} />
                        </span>
                      ) : col}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((profile) => (
                  <tr key={profile.id} className={styles.tr}>
                    <td className={styles.td}>
                      <Link href="#" style={{ fontSize: "12px" }}>{profile.name}</Link>
                    </td>
                    <td className={styles.td} style={{ color: "#323130" }}>{profile.id}</td>
                    <td className={styles.td}>
                      <Link href="#" style={{ fontSize: "12px", color: "#0078d4" }}>{profile.mtdCharges}</Link>
                    </td>
                    <td className={styles.td}>
                      <Link href="#" style={{ fontSize: "12px", color: "#0078d4" }}>{profile.lastMonthCharges}</Link>
                    </td>
                    <td className={styles.td}>{profile.myRole}</td>
                    <td className={styles.td}>
                      <span style={{ display: "inline-flex", alignItems: "center", gap: "5px" }}>
                        <CheckmarkCircleFilled style={{ fontSize: "14px", color: "#107c10" }} />
                        <span style={{ fontSize: "12px", color: "#323130" }}>{profile.status}</span>
                      </span>
                    </td>
                    <td className={styles.td} style={{ textAlign: "right" }}>
                      <button className={styles.moreBtn} aria-label="More options">
                        <MoreHorizontalRegular />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Pagination footer */}
      <div className={styles.phFooter}>
        <button disabled className={styles.phPageBtn}>&lt; Previous</button>
        <span style={{ fontSize: "12px", color: "#323130", margin: "0 4px" }}>Page</span>
        <div className={styles.phPageDropdown}>
          <span style={{ fontSize: "12px", color: "#323130" }}>{currentPage}</span>
          <ChevronDownRegular style={{ position: "absolute", right: "5px", fontSize: "11px", color: "#605e5c" }} />
        </div>
        <span style={{ fontSize: "12px", color: "#323130" }}>of {totalPages}</span>
        <button disabled className={styles.phPageBtn}>Next &gt;</button>
      </div>
    </>
  );
};

// ─── Generic placeholder page ─────────────────────────────────────────────────
const PlaceholderPage: React.FC<{ icon: React.ReactNode; title: string }> = ({ icon, title }) => {
  const styles = useStyles();
  return (
    <>
      <div className={styles.commandBar} role="toolbar">
        <button className={styles.cmdBtn}><ArrowClockwiseRegular className={styles.cmdBtnIcon} /><span>Refresh</span></button>
        <div className={styles.cmdDivider} />
        <button className={styles.cmdBtn}><QuestionCircleRegular className={styles.cmdBtnIcon} /><span>Help</span></button>
      </div>
      <div className={styles.contentOuter}>
        <div className="az-page-enter">
          <div className={styles.placeholder}>
            <div className={styles.placeholderIcon}>{icon}</div>
            <div className={styles.placeholderTitle}>{title}</div>
            <div className={styles.placeholderDesc}>
              This section is available in your billing account. Navigate using the sidebar.
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────
const BillingInvoices: React.FC = () => {
  const styles = useStyles();
  const [activePage, setActivePage] = useState<PageKey>("invoices");
  usePageAnimation();

  const navigate = (key: PageKey) => setActivePage(key);

  const renderContent = () => {
    switch (activePage) {
      case "invoices":        return <InvoicesPage />;
      case "paymentMethods":  return <PaymentMethodsPage />;
      case "paymentHistory":  return <PaymentHistoryPage />;
      case "billingProfiles": return <BillingProfilesPage />;
      default:
        return <PlaceholderPage icon={PAGE_ICONS[activePage]} title={PAGE_LABELS[activePage]} />;
    }
  };

  return (
    <FluentProvider theme={webLightTheme}>
      <div className={styles.appRoot}>
        <div className={styles.shell}>
          {/* ── Sidebar ── */}
          <nav className={styles.sidebar} aria-label="Billing navigation">
            <div className={styles.sidebarHeader}>
              <div className={styles.sidebarBrand}>
                <div className={styles.sidebarBrandIcon}><ReceiptRegular /></div>
                <div>
                  <div className={styles.sidebarBrandName}>Chigozie Iloelunachi</div>
                  <div className={styles.sidebarBrandSub}>Billing account</div>
                </div>
              </div>
              <Input className={styles.sidebarSearch} size="small" placeholder="Search"
                contentBefore={<SearchRegular style={{ fontSize: "14px", color: "#605e5c" }} />} />
            </div>

            <div className={styles.navScroll}>
              {/* Quick access — always open */}
              <div className={styles.navGroup}>
                <div className={styles.navGroupHeader}>
                  <span className={styles.navGroupIcon}><ChevronDownRegular /></span>
                  Quick access
                </div>
                {QUICK_ACCESS.map((item) => {
                  const isActive = activePage === item.key;
                  return (
                    <div key={item.key}
                      className={`${styles.navItem} ${isActive ? styles.navItemActive : ""}`}
                      onClick={() => navigate(item.key)}
                      role="button"
                      aria-current={isActive ? "page" : undefined}
                    >
                      {isActive && (
                        <span style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: "2px", backgroundColor: "#0078d4" }} />
                      )}
                      <span className={styles.navItemIcon}>{item.icon}</span>
                      {item.label}
                    </div>
                  );
                })}
              </div>

              {NAV_GROUPS.map((g) => (
                <NavGroupComp key={g.label} group={g} activePage={activePage} onNavigate={navigate} />
              ))}
            </div>

            <div className={styles.sidebarFooter}>
            
            </div>
          </nav>

          {/* ── Main ── */}
          <main className={styles.main}>
            {/* Breadcrumb */}
            <div className={styles.breadcrumb}>
              <HomeRegular style={{ fontSize: "13px" }} />
              <Link href="#" style={{ fontSize: "12px" }}>Home</Link>
              <ChevronRightRegular style={{ fontSize: "11px", color: "#a19f9d" }} />
              <Link href="#" style={{ fontSize: "12px" }}>Chigozie Iloelunachi</Link>
              {activePage !== "overview" && (
                <>
                  <ChevronRightRegular style={{ fontSize: "11px", color: "#a19f9d" }} />
                  <span style={{ fontSize: "12px" }}>{PAGE_LABELS[activePage]}</span>
                </>
              )}
            </div>

            {/* Blade header */}
            <div className={styles.bladeHeader}>
              <div className={styles.bladeIconTitle}>
                <div className={styles.bladeIcon}>
                  {React.cloneElement(PAGE_ICONS[activePage] as React.ReactElement, { style: { fontSize: "18px" } })}
                </div>
                <Text as="h1" className={styles.bladeTitle}>
                  Chigozie Iloelunachi{" "}
                  <span className={styles.bladeTitleMuted}>| {PAGE_LABELS[activePage]}</span>
                </Text>
              </div>
              <div className={styles.bladeSubtitle}>Billing account</div>
            </div>

            {/* Page content */}
            <div key={activePage} style={{ display: "flex", flexDirection: "column", flex: 1, overflow: "hidden" }}>
              {renderContent()}
            </div>
          </main>
        </div>
      </div>
    </FluentProvider>
  );
};

export default BillingInvoices;